// Single source of truth for a claim's displayed status: label, icon and
// color. Used by both the claims list (AllClaims.vue) and the single-claim
// page (SingleClaim.vue) so a claim only ever shows one status badge instead
// of stacking approved/verified/vetted/rejected/payment badges side by side.
//
// `variant` is a shared color vocabulary consumed by both views —
// AllClaims.vue applies it as `status-badge.<variant>`, SingleClaim.vue as
// `badge-soft-<variant>`.

const STATUS = {
  paid:      { label: "Paid",                   variant: "success", icon: "fe-check-square"  },
  processed: { label: "Processed for Payment",  variant: "indigo",  icon: "fe-credit-card"    },
  rejected:  { label: "Rejected",               variant: "danger",  icon: "fe-x-circle"       },
  suspended: { label: "Suspended",              variant: "dark",    icon: "fe-pause-circle"   },
  approved:  { label: "Approved",               variant: "success", icon: "fe-check-circle"  },
  forwarded: { label: "Forwarded to ES",        variant: "forwarded", icon: "fe-send"           },
  verified:  { label: "Verified",               variant: "info",    icon: "fe-check-square"   },
  vetted:    { label: "Vetted",                 variant: "indigo",  icon: "fe-shield"          },
  pending:   { label: "Pending",                variant: "warning", icon: "fe-clock"           },
  draft:     { label: "Draft",                  variant: "draft",   icon: "fe-edit-3"          },
};

/**
 * Resolves a claim down to the one status that should be shown for it.
 * Priority: draft > payment status > claim-level status (rejected/suspended/approved)
 * > review progress (verified/vetted) > pending.
 */
export function getClaimStatus(claim) {
  if (!claim) return { key: "pending", ...STATUS.pending };

  // Status 2: saved by the facility but not yet submitted for vetting (only the facility sees it).
  if (claim.status !== null && Number(claim.status) === 2) return { key: "draft", ...STATUS.draft };

  const payment = claim.paymentorders && claim.paymentorders.length ? claim.paymentorders[0] : null;
  if (payment && payment.status === "paid") return { key: "paid", ...STATUS.paid };
  if (payment && payment.status === "pending") return { key: "processed", ...STATUS.processed };

  if (claim.status === -2) return { key: "rejected", ...STATUS.rejected };
  if (claim.status === -1) return { key: "suspended", ...STATUS.suspended };
  if (claim.status === 1) return { key: "approved", ...STATUS.approved };
  // Status 3: verified and forwarded to the Executive Secretary for approval.
  if (claim.status !== null && Number(claim.status) === 3) return { key: "forwarded", ...STATUS.forwarded };
  if (claim.verified_by_id) return { key: "verified", ...STATUS.verified };
  if (claim.checked_by_id) return { key: "vetted", ...STATUS.vetted };
  if (claim.status === 0) return { key: "rejected", ...STATUS.rejected };

  return { key: "pending", ...STATUS.pending };
}
